import {isBiologicsEnabled} from 'antibodies/util/biologicsCapability';
import isArchived from 'archive/isArchived';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  AntibodyComponentSchemaRole,
  AntibodyEnforcedSchemaRole,
  AntibodyRelatedSchemaRole,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import {SCHEMAS_WITH_INTERFACES_QUERY} from 'schemas/schema-interface/SchemaInterfaceConstants';
import {StaticSchemaInterfaceSummary} from 'schemas/schema-interface/SchemaInterfaceConstants';
import {ImplementedStaticSchemaInterface} from 'schemas/SchemaConstants';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {SchemasWithInterfacesQuery} from '../../schemas/schema-interface/__generated/SchemasWithInterfacesQuery';
import {SchemasWithInterfacesQuery_tagSchemas_TagSchemaNode_interfaces_SchemaInterfaceNode_StaticSchemaInterfaceNode as SchemaInterfaceNode} from '../../schemas/schema-interface/__generated/SchemasWithInterfacesQuery';
import {AntibodyComponentType} from '../AntibodyConstants';

export const ANTIBODY_SCHEMA_INTERFACE_ID = 'ssi_antibody';

export async function requeryAntibodyConfigurationTable(): Promise<void> {
  const cached = readQuerySafe<SchemasWithInterfacesQuery>({query: SCHEMAS_WITH_INTERFACES_QUERY});
  if (!cached) {
    return;
  }
  await doQuerySafe<SchemasWithInterfacesQuery>(
    {query: SCHEMAS_WITH_INTERFACES_QUERY},
    {fetchPolicy: 'network-only'}
  );
}

export enum AntibodyComponentSchemaInterfaceApiId {
  AA_DOMAIN_ID = 'ssi_aadomain',
  AA_SUB_ELEMENT_ID = 'ssi_aasubelement',
  DNA_DOMAIN_ID = 'ssi_dnadomain',
  DNA_SUB_ELEMENT_ID = 'ssi_dnasubelement',
  AA_CHAIN_ID = 'ssi_aachain',
  DNA_CHAIN_ID = 'ssi_dnachain',
  AA_PAIR_ID = 'ssi_aapair',
  DNA_PAIR_ID = 'ssi_dnapair',
  ANTIGEN_ID = 'ssi_antigen',
}

// Keep in sync with benchling/antibodies/schema/core/constants.py
export const ANTIBODY_COMPONENT_TYPE_TO_INTERFACE_ID: Record<AntibodyComponentType, string> = {
  [AntibodyComponentType.AA_DOMAIN]: AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID,
  [AntibodyComponentType.DNA_DOMAIN]: AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID,
  [AntibodyComponentType.AA_CHAIN]: AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID,
  [AntibodyComponentType.DNA_CHAIN]: AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID,
  [AntibodyComponentType.AA_PAIR]: AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID,
  [AntibodyComponentType.DNA_PAIR]: AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID,
  [AntibodyComponentType.ANTIGEN]: AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID,
};

export const ANTIBODY_COMPONENT_INTERFACE_ID_TO_SCHEMA_ROLE: Record<
  AntibodyComponentSchemaInterfaceApiId,
  AntibodyComponentSchemaRole | AntibodyRelatedSchemaRole
> = {
  [AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID]: AntibodyComponentSchemaRole.AA_DOMAIN,
  [AntibodyComponentSchemaInterfaceApiId.AA_SUB_ELEMENT_ID]: AntibodyRelatedSchemaRole.AA_SUB_ELEMENT,
  [AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID]: AntibodyComponentSchemaRole.AA_CHAIN,
  [AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID]: AntibodyRelatedSchemaRole.DNA_DOMAIN,
  [AntibodyComponentSchemaInterfaceApiId.DNA_SUB_ELEMENT_ID]: AntibodyRelatedSchemaRole.DNA_SUB_ELEMENT,
  [AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID]: AntibodyRelatedSchemaRole.DNA_CHAIN,
  [AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID]: AntibodyRelatedSchemaRole.AA_PAIR,
  [AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID]: AntibodyRelatedSchemaRole.DNA_PAIR,
  [AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID]: AntibodyRelatedSchemaRole.ANTIGEN,
};

export const ANTIBODY_SCHEMA_ROLE_TO_INTERFACE_ID: Record<
  AntibodyComponentSchemaRole | AntibodyRelatedSchemaRole,
  AntibodyComponentSchemaInterfaceApiId
> = {
  [AntibodyComponentSchemaRole.AA_CHAIN]: AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID,
  [AntibodyComponentSchemaRole.AA_DOMAIN]: AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID,
  [AntibodyRelatedSchemaRole.AA_SUB_ELEMENT]: AntibodyComponentSchemaInterfaceApiId.AA_SUB_ELEMENT_ID,
  [AntibodyRelatedSchemaRole.DNA_SUB_ELEMENT]: AntibodyComponentSchemaInterfaceApiId.DNA_SUB_ELEMENT_ID,
  [AntibodyRelatedSchemaRole.DNA_CHAIN]: AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID,
  [AntibodyRelatedSchemaRole.DNA_DOMAIN]: AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID,
  [AntibodyRelatedSchemaRole.AA_PAIR]: AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID,
  [AntibodyRelatedSchemaRole.DNA_PAIR]: AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID,
  [AntibodyRelatedSchemaRole.ANTIGEN]: AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID,
};

export const ANTIBODY_ENFORCED_ROLE_TO_INTERFACE_ID: Record<
  AntibodyEnforcedSchemaRole,
  AntibodyComponentSchemaInterfaceApiId | null
> = {
  [AntibodyEnforcedSchemaRole.AA_CHAIN]: AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID,
  [AntibodyEnforcedSchemaRole.AA_DOMAIN]: AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID,
  [AntibodyEnforcedSchemaRole.AA_PAIR]: AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID,
  [AntibodyEnforcedSchemaRole.AA_SUB_ELEMENT]: AntibodyComponentSchemaInterfaceApiId.AA_SUB_ELEMENT_ID,
  [AntibodyEnforcedSchemaRole.ANTIBODY]: null,
  [AntibodyEnforcedSchemaRole.ANTIGEN]: AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID,
  [AntibodyEnforcedSchemaRole.DNA_CHAIN]: AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID,
  [AntibodyEnforcedSchemaRole.DNA_DOMAIN]: AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID,
  [AntibodyEnforcedSchemaRole.DNA_PAIR]: AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID,
  [AntibodyEnforcedSchemaRole.DNA_SUB_ELEMENT]: AntibodyComponentSchemaInterfaceApiId.DNA_SUB_ELEMENT_ID,
};

// Component interface IDs for antibody enforced data model configuration
export type AntibodyComponentSchemaInterfaceId =
  | typeof AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.AA_SUB_ELEMENT_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.DNA_SUB_ELEMENT_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID;

export const AntibodyComponentSchemaInterfaceIds: ReadonlyArray<AntibodyComponentSchemaInterfaceId> = [
  AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID,
  AntibodyComponentSchemaInterfaceApiId.AA_SUB_ELEMENT_ID,
  AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID,
  AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID,
  AntibodyComponentSchemaInterfaceApiId.DNA_SUB_ELEMENT_ID,
  AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID,
  AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID,
  AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID,
  AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID,
];

export const ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME: Record<
  AntibodyComponentSchemaInterfaceApiId,
  string
> = {
  [AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID]: 'AA domain',
  [AntibodyComponentSchemaInterfaceApiId.AA_SUB_ELEMENT_ID]: 'AA sub-element',
  [AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID]: 'AA chain',
  [AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID]: 'DNA domain',
  [AntibodyComponentSchemaInterfaceApiId.DNA_SUB_ELEMENT_ID]: 'DNA sub-element',
  [AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID]: 'DNA chain',
  [AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID]: 'AA variable pair',
  [AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID]: 'DNA variable pair',
  [AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID]: 'Antigen',
};

export function isAntibodyComponentSchemaInterfaceId(id: string): id is AntibodyComponentSchemaInterfaceId {
  return AntibodyComponentSchemaInterfaceIds.includes(id as AntibodyComponentSchemaInterfaceId);
}

export interface AntibodyDomainSchemaFieldSpec {
  schemaOrInterfaceId: string | null;
  domainType: AntibodyDomainType;
  specificity: string | null;
}

export interface AntibodyChainSchemaFieldSpec {
  schemaOrInterfaceId: string | null;
  label: string;
}

export interface ImplementedAntibodyInterface extends ImplementedStaticSchemaInterface {
  interfaceId: typeof ANTIBODY_SCHEMA_INTERFACE_ID;
  formatId: string;
  domainSchemaFieldSpecs: Array<AntibodyDomainSchemaFieldSpec>;
  chainSchemaFieldSpecs: Array<AntibodyChainSchemaFieldSpec>;
}

export type DraftImplementedAntibodyInterface = Pick<ImplementedAntibodyInterface, 'interfaceId'> &
  Partial<ImplementedAntibodyInterface>;

export function isAntibodyImplementedInterface(
  implementedInterface: ImplementedStaticSchemaInterface
): implementedInterface is ImplementedAntibodyInterface | DraftImplementedAntibodyInterface {
  return ANTIBODY_SCHEMA_INTERFACE_ID === implementedInterface.interfaceId;
}

export function isCompleteImplementedAntibodyInterface(
  implementedInterface: DraftImplementedAntibodyInterface
): implementedInterface is ImplementedAntibodyInterface {
  const hasCompleteComponentSchemaSpecs =
    implementedInterface.domainSchemaFieldSpecs != null &&
    implementedInterface.domainSchemaFieldSpecs.every((spec) => spec.schemaOrInterfaceId != null) &&
    implementedInterface.chainSchemaFieldSpecs != null &&
    implementedInterface.chainSchemaFieldSpecs.every((spec) => spec.schemaOrInterfaceId != null);
  return (
    implementedInterface.interfaceId === ANTIBODY_SCHEMA_INTERFACE_ID &&
    implementedInterface.formatId != null &&
    implementedInterface.domainSchemaFieldSpecs != null &&
    (!isBiologicsEnabled() || hasCompleteComponentSchemaSpecs)
  );
}

export type TagSchemaWithInterfacesForAntibodyConfig = TagSchemaSummaryGraphQL & {
  interfaces: Array<SchemaInterfaceNode>;
};

/**
 * Filter given schemas to only include the AA sequence schemas that implement the given antibody component SSI.
 */
export function filterAntibodyComponentSchemas(
  allSchemas: Array<TagSchemaWithInterfacesForAntibodyConfig>,
  antibodyComponentType: AntibodyComponentType
): Array<TagSchemaWithInterfacesForAntibodyConfig> {
  if (!isBiologicsEnabled()) {
    return [];
  }
  return allSchemas.filter(
    (schema) =>
      // @ts-expect-error TS does not believe that TagSchemaSupportedFolderItemType overlaps with
      // ModelTypeConstants despite the former being comprised entirely of members of the latter
      schema.folderItemType === ModelTypeConstants.PROTEIN &&
      !isArchived(schema) &&
      schema.interfaces
        .map((schemaInterface) => schemaInterface.id)
        .includes(ANTIBODY_COMPONENT_TYPE_TO_INTERFACE_ID[antibodyComponentType])
  );
}

export function getAntibodySchemaInterfaceFromId(
  id: AntibodyComponentSchemaInterfaceApiId
): StaticSchemaInterfaceSummary {
  return {
    id,
    name: ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[id],
  };
}
